import { useState, useMemo, useRef, useEffect } from 'react';
import { Store as StoreIcon, ChevronDown, Search, Check } from 'lucide-react';
import type { Store } from '../../api';

export type StoreFilterValue = string[] | null; // null = All restaurants

const MAX_VISIBLE = 100;

interface StoreFilterProps {
  stores: Store[];
  value: StoreFilterValue;
  onChange: (value: StoreFilterValue) => void;
  disabled?: boolean;
}

export function StoreFilter({ stores, value, onChange, disabled = false }: StoreFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) searchRef.current?.focus();
  }, [isOpen]);

  const isAll = value === null;
  const selectedCount = value?.length ?? 0;
  const selectedSet = useMemo(() => new Set(value ?? []), [value]);

  const filteredStores = useMemo(() => {
    if (!search.trim()) return stores;
    const q = search.toLowerCase();
    return stores.filter(
      (s) =>
        s.internalStoreNumber.toLowerCase().includes(q) ||
        s.storeName.toLowerCase().includes(q) ||
        (s.champsNumber && s.champsNumber.toLowerCase().includes(q))
    );
  }, [stores, search]);

  // Show selected stores first, then cap unselected to MAX_VISIBLE for DOM perf
  const { visible, hiddenCount } = useMemo(() => {
    const selected: Store[] = [];
    const unselected: Store[] = [];
    for (const s of filteredStores) {
      if (selectedSet.has(s.internalStoreNumber)) {
        selected.push(s);
      } else {
        unselected.push(s);
      }
    }
    const cap = Math.max(0, MAX_VISIBLE - selected.length);
    return {
      visible: [...selected, ...unselected.slice(0, cap)],
      hiddenCount: Math.max(0, unselected.length - cap),
    };
  }, [filteredStores, selectedSet]);

  const displayLabel = isAll
    ? 'All restaurants'
    : `${selectedCount} restaurant${selectedCount !== 1 ? 's' : ''}`;

  const handleSelectAll = () => {
    onChange(null);
    setIsOpen(false);
  };

  const handleToggleStore = (storeId: string) => {
    if (value === null) {
      onChange([storeId]);
    } else {
      const next = value.includes(storeId)
        ? value.filter((id) => id !== storeId)
        : [...value, storeId];
      onChange(next.length === 0 ? null : next);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        data-testid="complaints-select-stores"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Restaurants: ${displayLabel}`}
        className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[44px] min-w-[140px] text-sm text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <StoreIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
        <span className="truncate min-w-0">{displayLabel}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-gray-500 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={handleClose} />

          <div
            role="listbox"
            aria-label="Restaurant filter"
            className="absolute left-0 top-full mt-1 z-20 w-72 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden"
          >
            <button
              role="option"
              aria-selected={isAll}
              onClick={handleSelectAll}
              data-testid="complaints-store-option-all"
              className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                isAll ? 'text-gray-900 bg-gray-100 font-medium' : 'text-gray-700'
              }`}
            >
              {isAll && <Check className="w-4 h-4" />}
              <span className={!isAll ? 'ml-6' : ''}>
                All restaurants
                {stores.length > MAX_VISIBLE && (
                  <span className="text-gray-400 font-normal"> ({stores.length.toLocaleString()})</span>
                )}
              </span>
            </button>

            <div className="border-t border-gray-100 px-2 py-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  placeholder={stores.length > MAX_VISIBLE ? 'Type to search restaurants...' : 'Search restaurants...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="complaints-store-search"
                  className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                />
              </div>
            </div>

            <div className="max-h-60 overflow-y-auto border-t border-gray-100">
              {filteredStores.length === 0 ? (
                <div className="px-4 py-6 text-sm text-gray-500 text-center">No restaurants match</div>
              ) : (
                <>
                  {visible.map((store) => {
                    const selected = selectedSet.has(store.internalStoreNumber);
                    return (
                      <button
                        key={store.internalStoreNumber}
                        role="option"
                        aria-selected={selected}
                        onClick={() => handleToggleStore(store.internalStoreNumber)}
                        data-testid={`complaints-store-option-${store.internalStoreNumber}`}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                          selected ? 'text-gray-900 bg-gray-50' : 'text-gray-700'
                        }`}
                      >
                        {selected ? <Check className="w-4 h-4 flex-shrink-0" /> : <span className="w-4" />}
                        <span className="truncate">
                          {store.storeName}{' '}
                          <span className="text-gray-400">
                            #{store.champsNumber || store.internalStoreNumber}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                  {hiddenCount > 0 && (
                    <div className="px-4 py-3 text-xs text-gray-400 text-center border-t border-gray-100">
                      {hiddenCount.toLocaleString()} more — type to narrow results
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
